import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { CheckCircle2, Droplets, Flame, Sun, Wrench, Snowflake, ClipboardCheck } from "lucide-react";
import { requestServiceBooking } from "@/lib/service-booking.functions";

const TITLE = "Boka verkstad & fukttest i Kalix | AK Husbil & Fritid";
const DESCRIPTION =
  "Boka fukttest, gasolkontroll, solcellsmontering, service eller vinterförvaring för husbil och husvagn i Kalix. Skicka förfrågan – vi svarar med tid och prisbild.";

export const Route = createFileRoute("/boka-service")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://www.akhusbilfritid.se/boka-service" }],
  }),
  component: BokaService,
});

const SERVICES = [
  {
    id: "Fukttest",
    icon: Droplets,
    desc: "Protokollförd mätning i väggar, golv och tak. Du får en rapport med mätvärden.",
    price: "från 1 495 kr",
  },
  {
    id: "Gasolkontroll",
    icon: Flame,
    desc: "Täthetsprovning av gasolsystem med intyg – krävs ofta av försäkringsbolag.",
    price: "från 995 kr",
  },
  {
    id: "Solceller & el",
    icon: Sun,
    desc: "Montering av solpanel, regulator, litiumbatteri och växelriktare.",
    price: "offert",
  },
  {
    id: "Service & reparation",
    icon: Wrench,
    desc: "Värmare, kylskåp, vatten, tätning, luckor och beslag.",
    price: "timpris 795 kr",
  },
  {
    id: "Vinterförvaring",
    icon: Snowflake,
    desc: "Avfuktad förvaring, avställning och kontroll inför säsong.",
    price: "från 1 900 kr/mån",
  },
  {
    id: "Köpbesiktning",
    icon: ClipboardCheck,
    desc: "Vi går igenom fordonet innan du köper – fukt, gasol, chassi och funktion.",
    price: "från 2 495 kr",
  },
];

function BokaService() {
  const submit = useServerFn(requestServiceBooking);
  const [service, setService] = React.useState(SERVICES[0].id);
  const [sending, setSending] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);

  const chooseService = (id: string) => {
    setService(id);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const year = String(fd.get("modelYear") || "").trim();
    setSending(true);
    try {
      await submit({
        data: {
          name: String(fd.get("name") || ""),
          email: String(fd.get("email") || ""),
          phone: String(fd.get("phone") || ""),
          vehicleType: (String(fd.get("vehicleType") || "husbil") as "husbil" | "husvagn" | "annat"),
          brandModel: String(fd.get("brandModel") || ""),
          modelYear: year ? Number(year) : undefined,
          serviceType: service,
          preferredPeriod: String(fd.get("preferredPeriod") || ""),
          message: String(fd.get("message") || ""),
        },
      });
      setDone(true);
      toast.success("Förfrågan skickad – vi hör av oss inom en arbetsdag.");
    } catch (err: any) {
      toast.error(err?.message || "Något gick fel. Ring oss gärna istället.");
    } finally {
      setSending(false);
    }
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Verkstad för husbil och husvagn i Kalix",
    serviceType: SERVICES.map((s) => s.id),
    provider: {
      "@type": "AutoRepair",
      name: "AK Husbil & Fritid",
      address: { "@type": "PostalAddress", addressLocality: "Kalix", addressRegion: "Norrbotten", addressCountry: "SE" },
      url: "https://www.akhusbilfritid.se",
    },
    areaServed: ["Kalix", "Luleå", "Piteå", "Boden", "Haparanda", "Norrbotten"],
  };

  return (
    <main className="pb-24">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <section className="container-edge pt-10 md:pt-16">
        <p className="eyebrow">Verkstad · Kalix</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-5xl">
          Boka fukttest, gasolkontroll och service
        </h1>
        <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
          Vi jobbar med husbilar och husvagnar varje dag – och testar varje fordon vi säljer. Skicka
          en förfrågan och du får svar med tid och prisbild inom en arbetsdag.
        </p>
      </section>

      <section className="container-edge mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s) => {
          const Icon = s.icon;
          const active = service === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => chooseService(s.id)}
              aria-pressed={active}
              className={`min-h-[44px] rounded-2xl border p-4 text-left transition ${
                active ? "border-accent bg-accent/5 shadow-sm" : "border-border hover:border-accent/50"
              }`}
            >
              <span className="flex items-center gap-2 text-sm font-semibold">
                <Icon size={18} className="text-accent" aria-hidden="true" />
                {s.id}
              </span>
              <span className="mt-2 block text-sm text-muted-foreground">{s.desc}</span>
              <span className="mt-2 block text-xs font-medium text-accent">{s.price}</span>
            </button>
          );
        })}
      </section>

      <section className="container-edge mt-12 max-w-2xl scroll-mt-24">
        <form
          ref={formRef}
          onSubmit={onSubmit}
          className="scroll-mt-24 rounded-3xl border border-border bg-card p-5 md:p-7"
        >
          <h2 className="text-xl font-semibold">Din förfrågan</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Vald tjänst: <span className="font-medium text-foreground">{service}</span>
          </p>

          {done ? (
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-accent/30 bg-accent/5 p-4">
              <CheckCircle2 className="mt-0.5 shrink-0 text-accent" size={20} aria-hidden="true" />
              <div>
                <p className="font-medium">Tack! Förfrågan är mottagen.</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Vi återkommer med förslag på tid. Brådskande? Ring oss direkt.
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-5 grid gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Namn" name="name" required autoComplete="name" />
                <Field label="E-post" name="email" type="email" required autoComplete="email" />
                <Field label="Telefon" name="phone" type="tel" autoComplete="tel" />
                <label className="block text-sm">
                  <span className="font-medium">Fordonstyp</span>
                  <select
                    name="vehicleType"
                    className="mt-1.5 min-h-[44px] w-full rounded-xl border border-border bg-background px-3 text-base"
                  >
                    <option value="husbil">Husbil</option>
                    <option value="husvagn">Husvagn</option>
                    <option value="annat">Annat</option>
                  </select>
                </label>
                <Field label="Märke & modell" name="brandModel" placeholder="Rapido C86" />
                <Field label="Årsmodell" name="modelYear" type="number" placeholder="2019" />
              </div>
              <Field label="Önskad period" name="preferredPeriod" placeholder="Vecka 34–35 eller så snart möjligt" />
              <label className="block text-sm">
                <span className="font-medium">Beskriv ärendet</span>
                <textarea
                  name="message"
                  rows={5}
                  className="mt-1.5 w-full rounded-xl border border-border bg-background p-3 text-base"
                  placeholder="T.ex. fuktlukt vid bakvägg, vill komplettera med 200 W solpanel…"
                />
              </label>
              <button
                type="submit"
                disabled={sending}
                className="min-h-[48px] rounded-full bg-foreground px-6 text-sm font-semibold text-background transition hover:opacity-90 disabled:opacity-60"
              >
                {sending ? "Skickar…" : "Skicka förfrågan"}
              </button>
              <p className="text-xs text-muted-foreground">
                Vi använder uppgifterna enbart för att svara på din förfrågan.
              </p>
            </div>
          )}
        </form>
      </section>
    </main>
  );
}

function Field({
  label,
  name,
  type = "text",
  ...rest
}: { label: string; name: string; type?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block text-sm">
      <span className="font-medium">{label}</span>
      <input
        name={name}
        type={type}
        className="mt-1.5 min-h-[44px] w-full rounded-xl border border-border bg-background px-3 text-base"
        {...rest}
      />
    </label>
  );
}
